import rawAxios from 'axios';
import constants from '../configs/constants';
import reduxStore from '../utils/redux/store';
import authSlice from '../utils/redux/slice/authSlice';
import { clearProfileCache } from '../hooks/useClientData';

/**
 * A stalled request must fail, not hang forever.
 *
 * Axios defaults to timeout: 0 — no limit at all. So a connection that opened
 * and then stalled left the sign-in button reading "Signing in…" indefinitely:
 * the promise never settled, the `finally` that resets the button never ran,
 * no error was shown, and the only way out was reloading the page. Reported as
 * "it says logging in… (its been a minute)".
 *
 * These endpoints are fast — login answers in well under a second — so thirty
 * seconds is far beyond any legitimate slow path, and reaching it means
 * something is genuinely wrong. Failing then produces the error message the
 * screens already know how to show, and lets someone try again.
 */
const AUTH_TIMEOUT_MS = 30000;
const axios = rawAxios.create({ timeout: AUTH_TIMEOUT_MS });

const login = async (email, password) => {
  try {
    const { data: response } = await axios.post(
      `${constants.API_URL}/auth/login`,
        {
          username: email,
          password,
        },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return {
      user: response.data.user,
      token: response.data.token,
      refreshToken: response.data.refreshToken,
      permissions: response.data.permissions,
    };
  } catch (error) {
    console.log(`Error: ${error}`);
    throw error;
  }
};

const verifyToken = async () => {
  try {
    if (!reduxStore.store.getState().auth.token) {
      return -1;
    }
    const { data: response } = await axios.post(
      `${constants.API_URL}/auth/verify-token`,
      {},
      {
        headers: {
          Authorization: `Bearer ${reduxStore.store.getState().auth.token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return {
      user: response.data.user,
      token: response.data.token,
      refreshToken: response.data.refreshToken,
      permissions: response.data.permissions,
    };
  } catch (error) {
    //if 401, logout return -1
    //if not, return -2
    if (error.response.status === 401) {
      return -1;
    }
    return -2;
  }
};

const register = async (email, firstName, lastName, password) => {
  try {
    const { data } = await axios.post(
      `${constants.API_URL}/auth/register`,
      {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error) {
    console.log(`Error: ${error}`);
    throw error;
  }
};

const verifyEmail = async (email, otp) => {
  try {
    const { data } = await axios.post(
      `${constants.API_URL}/auth/verify-email`,
      {
        email,
        otp,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return {
      user: data.data.user,
      token: data.data.token,
      refreshToken: data.data.refreshToken,
      permissions: data.data.permissions,
    };
  } catch (error) {
    console.log(`Error: ${error}`);
    throw error;
  }
};

const forgotPassword = async (email) => {
  try {
    const { data } = await axios.post(
      `${constants.API_URL}/auth/forgot-password`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error) {
    console.log(`Error: ${error}`);
    throw error;
  }
};

const resetPassword = async (email, otp, newPassword) => {
  try {
    const { data } = await axios.post(
      `${constants.API_URL}/auth/reset-password`,
      {
        email,
        otp,
        new_password: newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error) {
    console.log(`Error: ${error}`);
    throw error;
  }
};

const resendVerification = async (email) => {
  try {
    const { data } = await axios.post(
      `${constants.API_URL}/auth/resend-verification`,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error) {
    console.log(`Error: ${error}`);
    throw error;
  }
};

const googleAuth = async (credential) => {
  try {
    const { data: response } = await axios.post(
      `${constants.API_URL}/auth/google`,
      { credential },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return {
      user: response.data.user,
      token: response.data.token,
      refreshToken: response.data.refreshToken,
      permissions: response.data.permissions,
    };
  } catch (error) {
    console.log(`Error: ${error}`);
    throw error;
  }
};

/**
 * Full logout: revoke the refresh token server-side (fire-and-forget — the UI
 * never waits on it), then clear the module profile cache and redux state.
 * The refresh token is captured BEFORE the redux purge. Callers handle
 * navigation themselves.
 */
/**
 * Try to exchange the stored refresh token for a fresh session.
 * Returns { token, refreshToken, user } on success; null when the token is
 * missing or rejected (401). Network/server errors are rethrown so callers
 * can distinguish "session dead" from "connection problem".
 */
const tryRefreshSession = async () => {
  const refreshToken = reduxStore.store.getState().auth?.refreshToken;
  if (!refreshToken) return null;
  try {
    const { data: response } = await axios.post(`${constants.API_URL}/auth/refresh`, {
      refreshToken,
    });
    const d = response?.data;
    return d?.token ? d : null;
  } catch (error) {
    if (error?.response?.status === 401 || error?.response?.status === 400) return null;
    throw error;
  }
};

const performLogout = ({ callServer = true } = {}) => {
  const refreshToken = reduxStore.store.getState().auth?.refreshToken;
  if (callServer && refreshToken) {
    axios
      .post(`${constants.API_URL}/auth/logout`, { refreshToken })
      .catch((e) => console.warn('logout revoke failed', e?.message));
  }
  clearProfileCache();
  reduxStore.store.dispatch(authSlice.actions.logout());
};

export const authActions = {
  login,
  verifyToken,
  register,
  verifyEmail,
  forgotPassword,
  resetPassword,
  resendVerification,
  googleAuth,
  performLogout,
  tryRefreshSession,
};
